import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import {
  getDownloadMenuItemCount,
  hasDownloadableFormats,
  isOriginalDownloadable,
  isProxyDownloadable,
} from '@iconik-io/shared-lib/src/utils/download/search_item';

import { agentApiClient } from '../../agent_api_client';
import type { AppDispatch, AppState } from '../../store/types';
import { DYNAMIC_ICON } from '../../ui/menus/menu/constants';
import separateMenuGroups from '../../utils/separate_menu_groups';
import type { ContextMenuItemDefinition } from '../context_menu/types';
import { options } from './context_menu_options';
import {
  type ContextItemsAttributes,
  onCustomAction,
} from './context_menu_utils';

export default function getOptions(
  state: AppState,
  contextItems: SearchItemEntity[],
  attributes: ContextItemsAttributes,
  withDownloadOption: boolean,
): ContextMenuItemDefinition[] {
  const { userRoles } = state.rolesReducer;
  const items: ContextMenuItemDefinition[] = [];
  const assetCustomActions = userRoles.canReadCustomActions
    ? (state.customActions.customActionsContexts.SHARED_ASSET ?? [])
    : [];
  const collectionCustomActions = userRoles.canReadCustomActions
    ? (state.customActions.customActionsContexts.SHARED_COLLECTION ?? [])
    : [];

  const { selected, isOnlyAssets } = attributes;

  const itemsCustomActionsGroup: ContextMenuItemDefinition[] = [];

  if (withDownloadOption) {
    const downloadSubitems = [];
    const allBlocked = contextItems.every((item) => item.is_blocked);
    if (userRoles.webCanDownloadProxy) {
      const downloadableProxies = contextItems.filter((item) =>
        isProxyDownloadable(item),
      );
      downloadSubitems.push(
        options.ENTITY_DOWNLOAD_PROXY(
          downloadableProxies,
          true,
          allBlocked || downloadableProxies.length === 0,
          getDownloadMenuItemCount({
            downloadItems: downloadableProxies,
            contextItems,
          }),
        ),
      );
    }
    if (
      userRoles.webCanDownloadOriginal &&
      userRoles.canReadFormats &&
      userRoles.canReadFiles
    ) {
      const downloadableOriginals = contextItems.filter((item) =>
        isOriginalDownloadable(item),
      );
      downloadSubitems.push(
        options.ENTITY_DOWNLOAD_ORIGINAL(
          downloadableOriginals,
          true,
          allBlocked || downloadableOriginals.length === 0,
          getDownloadMenuItemCount({
            downloadItems: downloadableOriginals,
            contextItems,
          }),
        ),
      );

      if (agentApiClient.hasFeature('DOWNLOAD_ALL_FORMATS')) {
        const itemsWithFormats = contextItems.filter((item) =>
          hasDownloadableFormats(item),
        );
        downloadSubitems.push(
          options.ENTITY_DOWNLOAD_ALL_FORMATS(
            itemsWithFormats,
            true,
            allBlocked || itemsWithFormats.length === 0,
            getDownloadMenuItemCount({
              downloadItems: itemsWithFormats,
              contextItems,
            }),
          ),
        );
      }
    }
    if (downloadSubitems.length > 1) {
      items.push(options.ENTITY_DOWNLOAD(downloadSubitems, true));
    }
    if (downloadSubitems.length === 1) {
      downloadSubitems[0].label = `Download ${downloadSubitems[0].label}`;
      items.push(downloadSubitems[0]);
    }
  }

  if (selected === 1) {
    (isOnlyAssets ? assetCustomActions : collectionCustomActions)
      .filter((customAction) => !customAction.disabled)
      .forEach((customAction) => {
        itemsCustomActionsGroup.push({
          label: customAction.title,
          icon: DYNAMIC_ICON,
          action: () => (dispatch: AppDispatch) =>
            onCustomAction(
              dispatch,
              contextItems,
              customAction,
              attributes.fromCollectionId,
            ),
          multiple: true,
        });
      });
  }

  const list = separateMenuGroups([items, itemsCustomActionsGroup]);

  return list;
}
